import type {EducationLevelDTO, LicenseDTO, UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {GenderDTO} from '@/ts/types/dto/gender-dto';
import type {Language} from '@/ts/types/component/localization.type';

/** Approval state for a single consent type, including whether re-approval is needed */
export interface ConsentStatus {
  approved: boolean;
  consentedVersion: string | null;
  needsReapproval: boolean;
}

/** Map of all consent types and their current approval states for a user */
export interface UserConsents {
  medicalDeviceConsent?: ConsentStatus | null;
  termsAndConditions?: ConsentStatus | null;
  privacyPolicy?: ConsentStatus | null;
  productImprovement?: ConsentStatus | null;
  research?: ConsentStatus | null;
  analyticsAnonymized?: ConsentStatus | null;
}

/** Platform role determining access level: regular user, full admin, or exports-only data admin */
export type UserRole = 'user' | 'admin' | 'data-admin';

/** Authenticated user's full profile returned by the /me endpoint */
export interface UserResponse {
  id: UuidDTO;
  email: string;
  name: string;
  role: UserRole;
  gender: GenderDTO;
  countryCode: string;
  birthYear: number;
  educationLevel: EducationLevelDTO;
  inProgressInterventionId: UuidDTO|null;
  marketingCodeExternalId: UuidDTO;
  registrationDate: string;
  /** Currently-active contract, or `null` once it has expired/lapsed (the authoritative "has access" signal) */
  activeContract?: Contract | null;
  /** Most recent contract regardless of status; retains the expired contract for display after `activeContract` goes null */
  lastContract?: Contract | null;
  /** Account lifecycle state (not plan expiry) */
  userState?: UserState;
  /**
   * Whether the account has a password of its own (IPD-3177). False for one created through
   * Google, where the password no longer opens it and the settings form asking for the current
   * one has nothing to ask about. True for an account created with a password, including one
   * later linked to Google.
   *
   * Optional only while the backend that sends it is not deployed everywhere the app runs;
   * absent, the form stays as it was.
   */
  hasPassword?: boolean;
  isEligibleForPurchase: boolean;
  consents?: UserConsents;
  organization?: {
    id: UuidDTO;
    name: string;
    createDate: string;
  };
  language?: Language;
  onboardingTourVersion?: number | null; // null = user has never seen the tour
  /**
   * Tri-state flag for the legal-docs sidebar hint:
   *   null  → user has never completed the onboarding wizard (hint stays hidden;
   *           the wizard itself covers the legal-docs location)
   *   false → wizard completed, hint pending (will show once on the next login,
   *           auto-dismiss after 5s, then flip to true)
   *   true  → hint has been shown to the user; never show again
   * BE backfills `true` for all users existing at deploy time (IPD-2517).
   */
  legalDocsHintShown?: boolean | null;
  /**
   * True while the IFU version published in Directus differs from the one stored against the user
   * (IPD-3216). Absent on any endpoint that does not answer the question, and absent rather than
   * false if the backend could not read the published version — so only an explicit `true` raises
   * the notice, and an absent field is "no answer", never "cleared".
   */
  ifuUpdateAvailable?: boolean | null;
  minFontSize?: number;
  colorMode?: string;
  highContrast?: boolean;
}

/** Request to approve or revoke a specific consent type */
export interface ConsentApprovalRequest {
  consentType: 'medical_device' | 'terms_and_conditions' | 'privacy_policy' | 'product_improvement' | 'research' | 'analytics_anonymized';
  approved: boolean;
}

/** A user's active or expired service contract tied to a license */
export interface Contract {
  id: UuidDTO;
  license: LicenseDTO;
  createDate: string;
  status: ContractStatus;
  paymentSubscription?: PaymentSubscription;
}

/** Status of a user's contract */
export enum ContractStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
}

/** Account lifecycle state returned by the /me endpoint */
export enum UserState {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ANONYMIZED = 'anonymized',
}

/** Recurring payment subscription details linked to a user's contract */
export interface PaymentSubscription {
  product?: ProductInfo;
  currentPeriodEnd?: string;
  currentStatus?: SubscriptionStatus;
  canceled?: boolean;
  externalSubscriptionId?: string;
}

/** Full subscription details returned after creating or retrieving a payment subscription */
export interface SubscriptionResponse {
  id: string;
  clientSecret: string | null;
  status: string;
  customerId: string;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  product: ProductInfo;
  publicKey: string;
}

/** Purchasable product metadata including pricing and currency */
export interface ProductInfo {
  name?: string;
  description?: string;
  price?: number;
  currency?: string;
  recurring?: ProductRecurrence | null;
}

/** Billing recurrence of a product's default price. Null/absent for one-time (non-recurring) products. */
export interface ProductRecurrence {
  interval: string;
  intervalCount: number;
}

/** Status of a user's payment subscription */
export enum SubscriptionStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
}

/** A program grouping a set of interventions available to the user */
export interface Program {
  id: UuidDTO;
  interventionIDs: UuidDTO[];
}

/** A license available for purchase, bundled with its product pricing info */
export interface PurchaseLicense {
  license: LicenseDTO;
  productInfo: ProductInfo;
}

/** Stored payment card details (brand, last 4 digits, expiration) */
export interface PaymentMethodInfo {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/** Response from creating a Stripe SetupIntent for saving a payment method */
export interface SetupIntentResponse {
  id: string;
  clientSecret: string;
  status: string;
  customerId: string;
  publicKey: string;
}
